import React, { useCallback, useEffect, useMemo } from 'react'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Box, Divider, Stack } from '@mui/material'
import { TABLE_WIDTH_STORAGE_PLACE, TABLE_WIDTH_FOUND } from 'lib/tableLib'
import {
  LOADER_CORRECT_STATES,
  LOADER_ERROR_STATES,
  LOADER_WARNING_STATES,
} from 'lib/loaderStates'
import { format } from 'date-fns'
import itLocale from 'date-fns/locale/it'
import { getDateIsoString } from 'lib/dateLib'

import DropArea from 'components/atoms/DropArea'
import Table from 'components/atoms/Table'
import ConfirmBar from 'components/atoms/ConfirmBar'
import DownloadBar from 'components/atoms/DownloadBar'
import TableHeaderCell from 'components/atoms/TableHeaderCell'
import FilterSelect from 'components/atoms/FilterSelect'
import SearchField from 'components/atoms/SearchField'
import DotCell from 'components/atoms/DotCell'
import TitleBar from 'components/atoms/TitleBar'
import UploadBar from 'components/atoms/UploadBar'
import FilterDateRange from 'components/atoms/FilterDateRange'
import DateCell from 'components/atoms/DateCell'
import LogIcon from 'components/atoms/LogIcon'

import useCarsFilters from 'hooks/useCarsFilters'
import useInputOutput, {
  BASE_FILE_UPLOAD_EXTENSIONS,
  validateExpectedLabel,
  validateNotEmpty,
} from 'hooks/useInputOutput'
import useLogs from 'hooks/useLogs'

import SessionsSearchBar from '../Sessions/SessionsSearchBar'
import SessionsDownloadTemplate from '../Sessions/SessionsDownloadTemplate'

import useLogistics from './useLogistics'

const createColumns = (t, cars, carsFilters) => {
  const {
    filters,
    filtersData,
    setStoragePlaceDateRange,
    setStoragePlaces,
    setStoragePlaceCheckState,
    setPlateQuery,
  } = carsFilters

  return [
    {
      accessor: 'plate',
      Header: (
        <TableHeaderCell
          label={t('logistics.table.plate')}
          filter={
            <SearchField
              data-testid="search-plate"
              placeholder={t('common.form.searchCarPlate')}
              value={filters.plateQuery}
              onChange={(event) => setPlateQuery(event.target.value)}
              fullWidth={false}
            />
          }
        />
      ),
    },
    {
      accessor: 'storagePlace',
      Header: (
        <TableHeaderCell
          label={t('logistics.table.storagePlace')}
          filter={
            <FilterSelect
              data-testid="search-storage-place"
              options={filtersData.storagePlaces}
              value={filters.storagePlaces}
              handleChange={setStoragePlaces}
              multiple
              hasSearchField
              searchLabel={t('common.form.searchCarStoragePlace')}
            />
          }
        />
      ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_STORAGE_PLACE,
        },
      },
    },
    {
      accessor: 'storagePlaceDate',
      Header: (
        <TableHeaderCell
          label={t('logistics.table.storagePlaceDate')}
          filter={
            <FilterDateRange
              value={[filters.storagePlaceDateFrom, filters.storagePlaceDateTo]}
              handleChange={setStoragePlaceDateRange}
            />
          }
        />
      ),
      Cell: DateCell,
    },
    {
      accessor: 'storagePlaceCheckState',
      Header: (
        <TableHeaderCell
          label={t('logistics.table.storagePlaceCheckState')}
          errorsNumber={
            cars.filter((car) =>
              LOADER_ERROR_STATES.includes(car.storagePlaceCheckState)
            ).length
          }
          filter={
            <FilterSelect
              options={filtersData.storagePlaceCheckState}
              value={filters.storagePlaceCheckState}
              handleChange={setStoragePlaceCheckState}
            />
          }
        />
      ),
      Cell: ({ value }) =>
        value ? (
          <DotCell
            color={
              LOADER_CORRECT_STATES.includes(value)
                ? 'primary'
                : LOADER_WARNING_STATES.includes(value)
                ? 'warning'
                : 'error'
            }
            label={t(`logistics.carStoragePlaceCheckState.${value}`)}
          />
        ) : (
          ''
        ),
      cellProps: {
        sx: {
          width: TABLE_WIDTH_FOUND,
        },
      },
    },
  ]
}

export default function Logistics({ contentIsReadOnly = false }) {
  const { t } = useTranslation()

  // const [fileName, setFileName] = useState()

  const { cars, updateCheckCars, confirmCars, isUpdating } = useLogistics()

  const { saveLogs, isFetching: isFetchingLogs } = useLogs()

  const handleValidation = useCallback(
    () =>
      !cars.some((car) =>
        LOADER_ERROR_STATES.includes(car?.storagePlaceCheckState)
      ),
    [cars]
  )
  const handleConfirm = useCallback(() => {
    confirmCars(cars, () => {
      // setFileName()
    })
  }, [cars, confirmCars])

  const carsFilters = useCarsFilters()
  const { filters, initFilters, setQuery, filterCars, resetFilters } =
    carsFilters

  const handleUpdate = useCallback(
    (cars) => {
      updateCheckCars(cars, () => {
        // setFileName(fileName)
        resetFilters()
      })
    },
    [resetFilters, updateCheckCars]
  )

  useEffect(() => {
    if (!cars.length) {
      return
    }

    initFilters(cars)
  }, [cars, initFilters])

  const readOnly = useMemo(() => isUpdating, [isUpdating])

  const filteredCars = useMemo(
    () => filterCars(cars, filters),
    [filterCars, cars, filters]
  )

  const columns = useMemo(
    () => createColumns(t, cars, carsFilters),
    [t, cars, carsFilters]
  )

  const parser = (rows) => {
    const COMMON_ERROR_WRONG_COLUMN_NAME =
      'common.error.fileUpload.wrongColumnName'

    validateExpectedLabel(
      rows,
      1,
      1,
      t('common.excel.plate'),
      COMMON_ERROR_WRONG_COLUMN_NAME,
      t
    )
    validateExpectedLabel(
      rows,
      1,
      2,
      t('common.excel.storagePlace'),
      COMMON_ERROR_WRONG_COLUMN_NAME,
      t
    )
    validateExpectedLabel(
      rows,
      1,
      3,
      t('common.excel.date'),
      COMMON_ERROR_WRONG_COLUMN_NAME,
      t
    )

    return rows
      .slice(2)
      .map((row, i) => {
        const storagePlaceDate = getDateIsoString(row[3], t)
        return row.length && validateNotEmpty(rows, i + 2, 1)
          ? {
              plate: row[1].toString().trim().toUpperCase(),
              storagePlace: row[2] || null,
              storagePlaceDate: storagePlaceDate || null,
            }
          : null
      })
      .filter((item) => item)
  }

  const { handleDrop, handleSelectFile, saveXls } = useInputOutput(
    handleUpdate,
    parser
  )

  const handleDownload = async () => {
    const rows = [
      [
        t('common.excel.plate'),
        t('common.excel.storagePlace'),
        t('common.excel.date'),
        ...(cars.length ? [t('common.excel.plateCheck')] : []),
      ],
      ...cars.map((car) => [
        car.plate,
        car.storagePlace,
        car.storagePlaceDate
          ? `${format(
              new Date(car.storagePlaceDate),
              t('common.date.formatDMY'),
              {
                locale: itLocale,
              }
            )}`
          : '',
        car.storagePlaceCheckState
          ? t(
              `logistics.carStoragePlaceCheckState.${car.storagePlaceCheckState}`
            )
          : '',
      ]),
    ]
    saveXls('loader', rows)
  }

  return (
    <div data-testid="logistics">
      <TitleBar
        title={t(`logistics.contentTitle`)}
        description={t(`logistics.contentDescription`)}
        rightColumn={
          <Stack direction="row" alignItems="center">
            <LogIcon
              disabled={isFetchingLogs}
              handleClick={() =>
                saveLogs(`logs_logistics`, {
                  area: t('common.logs.logistics'),
                })
              }
            />
            <Box pr={2} />
            <UploadBar
              // fileName={fileName}
              hideFileName
              accept={BASE_FILE_UPLOAD_EXTENSIONS}
              readOnly={readOnly || contentIsReadOnly}
              loading={readOnly}
              uploaded={!!cars.length}
              handleSelectFile={handleSelectFile}
            />
          </Stack>
        }
      />
      {cars.length ? (
        <>
          <Table
            id="cars-list"
            items={filteredCars}
            columns={columns}
            top={
              <>
                <ConfirmBar
                  component="div"
                  label={t(`logistics.confirmButton`)}
                  disabled={
                    !handleValidation() || readOnly || contentIsReadOnly
                  }
                  left={
                    <SessionsSearchBar
                      id="text"
                      label={t('logistics.platesUploaded')}
                      filteredLabel={t('common.filters.filteredPlates')}
                      cars={cars}
                      filteredCars={filteredCars}
                      query={filters.query}
                      handleChangeSearch={(event) =>
                        setQuery(event.target.value)
                      }
                    />
                  }
                  leftProps={{ sx: { width: 0.5 } }}
                  right={
                    <DownloadBar
                      readOnly={readOnly || contentIsReadOnly}
                      handleDownload={handleDownload}
                    />
                  }
                  handleConfirm={handleConfirm}
                />
                <Divider />
              </>
            }
          />
        </>
      ) : (
        <>
          <DropArea
            onDrop={handleDrop}
            disabled={readOnly || contentIsReadOnly}
            accept={BASE_FILE_UPLOAD_EXTENSIONS.toString()}
          />
          <SessionsDownloadTemplate
            label={t('logistics.downloadTemplateFile')}
            handleDownload={handleDownload}
            disabled={readOnly || contentIsReadOnly}
          />
        </>
      )}
    </div>
  )
}

Logistics.propTypes = {
  contentIsReadOnly: T.bool,
}
